import React, { useEffect, useState } from "react";
import { useMutation } from "@apollo/client/react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";
import PdfActionButton from "../PdfActionButton";
import useAuth from "../../hooks/useAuth";
import { UPDATE_PROJECT_MUTATION } from "../../constants/graphql/mutations";

const LABEL_TYPE_OPTIONS = [
  { value: "standard", label: "Standard" },
  { value: "mini", label: "Mini" },
  { value: "move_label", label: "Picture Label" },
];

const FULL_PAGE_VALUE = "full_page";

// Maps the frontend size key to the backend label_type param.
const LABEL_TYPE_MAP = {
  standard: "full_label",
  mini: "partial_label",
  move_label: "combined_move_label",
};

const getLabelType = size => LABEL_TYPE_MAP[size] || "full_label";

const styles = {
  closeButton: {
    position: "absolute",
    right: 0,
    top: 0,
  },
};

export default function AssetLabelingOptionsModal({ onClose, assetId, project }) {
  const { user } = useAuth();
  const [updateProject] = useMutation(UPDATE_PROJECT_MUTATION);
  const backendPreferences = project?.shipmentItemPdfPreferences || {};
  const [labelSize, setLabelSize] = useState(backendPreferences.size || "standard");
  const [selectedSheetTemplate, setSelectedSheetTemplate] = useState(
    backendPreferences.sheet_template || FULL_PAGE_VALUE
  );
  const [sheetTemplates, setSheetTemplates] = useState([]);

  useEffect(() => {
    if (sheetTemplates.length > 0) return;
    fetch(`/label_sheet_templates?token=${user.token}`)
      .then(r => r.json())
      .then(data => setSheetTemplates(data.templates || []))
      .catch(() => {});
  }, [sheetTemplates.length, user.token]);

  const persistPreferencesIfChanged = async () => {
    const changed =
      labelSize !== backendPreferences.size ||
      selectedSheetTemplate !== (backendPreferences.sheet_template || FULL_PAGE_VALUE);

    if (!changed || !project?.id) return;

    const shipmentItemPdfPreferences = {
      size: labelSize,
      count_by: backendPreferences.count_by || "rack_location",
      sheet_template: selectedSheetTemplate,
    };

    await updateProject({
      variables: {
        id: project.id,
        name: project.name,
        shipmentItemPdfPreferences,
      },
    });
  };

  const assetLabelQueryParams = () => {
    const extraQueryParams = {
      label_type: getLabelType(labelSize),
    };

    if (selectedSheetTemplate && selectedSheetTemplate !== FULL_PAGE_VALUE) {
      extraQueryParams.sheet_template = selectedSheetTemplate;
    }

    return extraQueryParams;
  };

  const handleGenerateAssetLabels = () => {
    onClose();
    persistPreferencesIfChanged();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box position="relative">
          <Typography fontSize={22} fontWeight={500} color="text.secondary" align="center">
            Item Label Options
          </Typography>
          <IconButton onClick={onClose} size="small" sx={styles.closeButton}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box py={1} display="flex" flexDirection="column" gap={2.5}>
          <Typography sx={{ textAlign: "center", fontSize: "16px", color: "text.primary", fontWeight: 400, lineHeight: 1.5 }}>
            Choose how you'd like your item labels generated
          </Typography>

          <FormControl fullWidth size="small">
            <InputLabel>Label Template</InputLabel>
            <Select
              value={labelSize}
              label="Label Template"
              onChange={e => setLabelSize(e.target.value)}
            >
              {LABEL_TYPE_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Page Setup</InputLabel>
            <Select
              value={selectedSheetTemplate}
              label="Page Setup"
              onChange={e => setSelectedSheetTemplate(e.target.value)}
            >
              <ListSubheader>Letter Sized Sheets</ListSubheader>
              <MenuItem value={FULL_PAGE_VALUE}>8½ × 11&quot; (1 per sheet)</MenuItem>
              {sheetTemplates.filter(t => t.printer_type === "sheet").map(t => (
                <MenuItem key={t.key} value={t.key}>{t.name}</MenuItem>
              ))}
              <ListSubheader>Thermal Printer Labels (Dymo)</ListSubheader>
              {sheetTemplates.filter(t => t.printer_type === "dymo").map(t => (
                <MenuItem key={t.key} value={t.key}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <PdfActionButton
          resourceType="asset_label_pdfs"
          resourceId={assetId}
          extraQueryParams={assetLabelQueryParams()}
          onOpenComplete={handleGenerateAssetLabels}
          variant="contained"
          color="primary"
        >
          Generate Item Labels
        </PdfActionButton>
      </DialogActions>
    </Dialog>
  );
}
